import { useEffect, useState, useRef, useMemo } from "react";
import { api } from "@/lib/api";
import { openStorageFile } from "@/lib/openStorageFile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Play, FileText, Volume2, Upload, ExternalLink, Image as ImageIcon, Link2, File, Pencil } from "lucide-react";

const kindIcon = { video: Play, audio: Volume2, pdf: FileText, image: ImageIcon, link: Link2, other: File };
const kindLabel = { video: "Vidéo", audio: "Audio", pdf: "PDF", image: "Image", link: "Lien internet", other: "Autre" };

const emptyForm = () => ({ title: "", description: "", kind: "video", url: "", file_path: "", visibility: "all", learner_id: null });

export default function CoachResources() {
  const [items, setItems] = useState([]);
  const [learners, setLearners] = useState([]);
  const [tab, setTab] = useState("all");
  const [sort, setSort] = useState("recent");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const editFileRef = useRef();

  const load = () => api.get("/coach/resources").then(({ data }) => setItems(data));
  useEffect(() => { load(); api.get("/coach/learners").then(({ data }) => setLearners(data)); }, []);

  const detectKind = (f, current) => {
    const t = (f.type || "").toLowerCase();
    const n = (f.name || "").toLowerCase();
    if (t.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(n)) return "image";
    if (t === "application/pdf" || n.endsWith(".pdf")) return "pdf";
    if (t.startsWith("audio/") || /\.(mp3|m4a|wav|ogg)$/i.test(n)) return "audio";
    if (t.startsWith("video/") || /\.(mp4|mov|webm)$/i.test(n)) return "video";
    return current;
  };

  const upload = async (e, setter, curKind) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/coach/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const detected = detectKind(f, curKind);
      setter((p) => ({ ...p, file_path: data.path, url: "", kind: detected }));
      toast.success("Fichier téléversé");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.visibility === "all") payload.learner_id = null;
      if (!payload.url && !payload.file_path) return toast.error("URL ou fichier requis");
      await api.post("/coach/resources", payload);
      toast.success("Ressource ajoutée");
      setOpen(false); setForm(emptyForm());
      load();
    } catch { toast.error("Erreur"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: editing.title, description: editing.description, kind: editing.kind,
        visibility: editing.visibility, learner_id: editing.visibility === "all" ? null : editing.learner_id,
        url: editing.url || null, file_path: editing.file_path || null,
      };
      if (!payload.url && !payload.file_path) return toast.error("URL ou fichier requis");
      await api.patch(`/coach/resources/${editing.id}`, payload);
      toast.success("Ressource modifiée");
      setEditing(null);
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette ressource ?")) return;
    await api.delete(`/coach/resources/${id}`);
    load();
  };

  const openResource = async (r) => {
    if (r.url) {
      window.open(r.url, "_blank", "noopener,noreferrer");
    } else if (r.file_path) {
      try { await openStorageFile(r.file_path); }
      catch (e) { toast.error(e.message || "Impossible d'ouvrir le fichier"); }
    }
  };

  const filtered = useMemo(() => {
    let list = tab === "all" ? [...items] : items.filter(i => i.kind === tab);
    const byTitle = (a, b) => (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" });
    if (sort === "az") list.sort(byTitle);
    else if (sort === "za") list.sort((a, b) => byTitle(b, a));
    else if (sort === "oldest") list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  }, [items, tab, sort]);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif-accent text-3xl sm:text-4xl">Ressources</h1>
          <p className="text-[#5C6561] mt-1">Vidéos, audios, PDFs, images, liens — pour tous ou pour un apprenant.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(emptyForm()); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-resource-button" className="dec-pill bg-[#D95D39] hover:bg-[#C04E2D] text-white">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle ressource
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Ajouter une ressource</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Titre</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="resource-form-title" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="resource-form-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="video">Vidéo</SelectItem>
                      <SelectItem value="audio">Audio</SelectItem>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="image">Image (JPEG / PNG)</SelectItem>
                      <SelectItem value="link">Lien internet</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Visibilité</Label>
                  <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les apprenants</SelectItem>
                      <SelectItem value="private">Privé (un apprenant)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.visibility === "private" && (
                <div><Label>Apprenant</Label>
                  <Select value={form.learner_id || ""} onValueChange={(v) => setForm({ ...form, learner_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{learners.map(l => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>URL externe (YouTube, Drive, lien internet…)</Label>
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value, file_path: "" })} placeholder="https://…" data-testid="resource-form-url" />
              </div>
              <div>
                <Label>Ou téléverser un fichier</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input ref={fileRef} type="file" onChange={(e) => upload(e, setForm, form.kind)} className="hidden" data-testid="resource-file-input"
                         accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,audio/*,video/*" />
                  <Button type="button" variant="outline" className="bg-white" onClick={() => fileRef.current?.click()} disabled={uploading}>
                    <Upload className="w-4 h-4 mr-2" /> {uploading ? "Téléversement…" : "Choisir un fichier"}
                  </Button>
                  {form.file_path && <span className="text-xs text-[#2A5B52] truncate">✓ Fichier prêt</span>}
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="resource-submit" className="bg-[#D95D39] hover:bg-[#C04E2D] text-white">Ajouter</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#F4EFEA] flex-wrap">
            <TabsTrigger value="all" data-testid="resource-filter-all">Toutes</TabsTrigger>
            <TabsTrigger value="video" data-testid="resource-filter-video">Vidéos</TabsTrigger>
            <TabsTrigger value="audio" data-testid="resource-filter-audio">Audio</TabsTrigger>
            <TabsTrigger value="pdf" data-testid="resource-filter-pdf">PDFs</TabsTrigger>
            <TabsTrigger value="image" data-testid="resource-filter-image">Images</TabsTrigger>
            <TabsTrigger value="link" data-testid="resource-filter-link">Liens</TabsTrigger>
            <TabsTrigger value="other" data-testid="resource-filter-other">Autres</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-full md:w-48" data-testid="resource-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Plus récentes</SelectItem>
            <SelectItem value="oldest">Plus anciennes</SelectItem>
            <SelectItem value="az">Alphabétique (A → Z)</SelectItem>
            <SelectItem value="za">Alphabétique (Z → A)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {filtered.length === 0 && <div className="col-span-full dec-card p-8 text-center text-[#5C6561]">Aucune ressource.</div>}
        {filtered.map((r) => {
          const Icon = kindIcon[r.kind] || FileText;
          const learner = learners.find(l => l.id === r.learner_id);
          return (
            <div key={r.id} className="dec-card p-4" data-testid={`resource-card-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg bg-[#FDF0EC] text-[#D95D39] flex items-center justify-center"><Icon className="w-4 h-4" /></div>
                <span className={`dec-pill text-xs px-2 py-0.5 ${r.visibility === "all" ? "status-completed" : "status-inprogress"}`}>
                  {r.visibility === "all" ? "Tous" : "Privé"}
                </span>
              </div>
              <div className="mt-3 font-semibold">{r.title}</div>
              <div className="text-xs text-[#5C6561] mt-0.5">{kindLabel[r.kind] || r.kind}</div>
              {r.description && <div className="text-xs text-[#5C6561] mt-1 line-clamp-2">{r.description}</div>}
              {learner && <div className="text-xs text-[#5C6561] mt-2">→ {learner.first_name} {learner.last_name}</div>}
              <div className="mt-3 flex gap-2 items-center">
                <button onClick={() => openResource(r)} className="text-xs text-[#D95D39] font-medium inline-flex items-center gap-1" data-testid={`open-resource-${r.id}`}>
                  <ExternalLink className="w-3 h-3" /> Ouvrir
                </button>
                <button onClick={() => setEditing({ ...r, learner_id: r.learner_id || null, url: r.url || "", file_path: r.file_path || "" })} className="text-xs text-[#2A5B52] inline-flex items-center gap-1" data-testid={`edit-resource-${r.id}`}>
                  <Pencil className="w-3 h-3" /> Modifier
                </button>
                <button onClick={() => remove(r.id)} className="ml-auto text-xs text-red-500 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Supprimer</button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier la ressource</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div><Label>Titre</Label><Input required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="resource-edit-title" /></div>
              <div><Label>Description</Label><Textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                  <Select value={editing.kind} onValueChange={(v) => setEditing({ ...editing, kind: v })}>
                    <SelectTrigger data-testid="resource-edit-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="video">Vidéo</SelectItem>
                      <SelectItem value="audio">Audio</SelectItem>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="image">Image (JPEG / PNG)</SelectItem>
                      <SelectItem value="link">Lien internet</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Visibilité</Label>
                  <Select value={editing.visibility} onValueChange={(v) => setEditing({ ...editing, visibility: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les apprenants</SelectItem>
                      <SelectItem value="private">Privé (un apprenant)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editing.visibility === "private" && (
                <div><Label>Apprenant</Label>
                  <Select value={editing.learner_id || ""} onValueChange={(v) => setEditing({ ...editing, learner_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{learners.map(l => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>URL externe</Label>
                <Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value, file_path: "" })} placeholder="https://…" data-testid="resource-edit-url" />
              </div>
              <div>
                <Label>Ou remplacer par un fichier</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input ref={editFileRef} type="file" onChange={(e) => upload(e, setEditing, editing.kind)} className="hidden"
                         accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,audio/*,video/*" />
                  <Button type="button" variant="outline" className="bg-white" onClick={() => editFileRef.current?.click()} disabled={uploading}>
                    <Upload className="w-4 h-4 mr-2" /> {uploading ? "Téléversement…" : "Choisir un fichier"}
                  </Button>
                  {editing.file_path && <span className="text-xs text-[#2A5B52] truncate">✓ Fichier</span>}
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="resource-edit-submit" className="bg-[#D95D39] hover:bg-[#C04E2D] text-white">Enregistrer</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
